// src/services/api/http.js
import axios from "axios";

/**
 * Base URLs
 * - CRA env: REACT_APP_*
 * - Vite env: VITE_*
 * - Fallback to hostname defaults
 */
const hostname = typeof window !== "undefined" ? window.location.hostname : "";

function getViteEnv() {
  try {
    // eslint-disable-next-line no-new-func
    return Function(
      'try { return import.meta && import.meta.env ? import.meta.env : {}; } catch { return {}; }',
    )();
  } catch {
    return {};
  }
}

const viteEnv = getViteEnv();

// CRA replaces process.env.REACT_APP_* at build time, but does not install
// a browser-global `process`. A typeof-process guard discards the injected URL.
function getCraApiBaseUrl() {
  try { return process.env.REACT_APP_API_BASE_URL || ''; } catch { return ''; }
}
const ENV_API =
  getCraApiBaseUrl() ||
  viteEnv.VITE_API_BASE_URL ||
  viteEnv.VITE_API_URL ||
  "";

export const API_BASE_URL =
  ENV_API ||
  (hostname === "localhost" || hostname === "127.0.0.1"
    ? "http://localhost:4000"
    : hostname.includes("preprod")
      ? "https://api.gapsmania-sol.com"
      : "https://api.gapsmania-sol.com");

// ARCHITECTURE INVARIANT: the browser must never call the Python AI
// service (services/) directly — Node (backend/) is the sole AI-routing
// boundary (auth, tenant scoping, usage logging, rate limiting all live
// there). AI_BASE_URL previously defaulted to the FastAPI service's own
// origin (http://localhost:8000 / the deployed AI service URL) whenever
// REACT_APP_AI_BASE_URL wasn't set, which would let any code importing
// `aiClient` bypass Node entirely. It is now always pinned to the same
// Node origin as API_BASE_URL, regardless of env configuration.
export const AI_BASE_URL = API_BASE_URL;

// ---- AI Tutor timeout policy ----
// apiClient's default 25000ms (below) is fine for ordinary CRUD calls, but
// it previously ALSO applied to tutor chat / "Explain differently" — both
// of which can legitimately take longer than 25s once RAG retrieval,
// provider retries, and a cross-provider fallback are accounted for. That
// mismatch (browser gives up in 25s while Node/FastAPI budget up to
// AI_TUTOR_TOTAL_TIMEOUT_MS ~55s server-side — see backend/src/config/env.js
// and services/config.py) is what surfaced as "timeout of 25000ms exceeded".
// Kept deliberately larger than the backend's total budget, not unbounded.
export const AI_TUTOR_TIMEOUT_MS = 65000;

// Booklet Studio's per-block AI actions (Simplify/Rewrite/Expand/.../
// Translate) hit the SAME 25000ms-vs-backend-budget mismatch: Node's own
// call to the AI service already budgets 45000ms (bookletEditor.service.js's
// callBlockAiAction), so the browser must wait at least that long. Discovered
// live ("timeout of 25000ms exceeded" on a real Simplify call) — same fix
// shape as AI_TUTOR_TIMEOUT_MS above, kept independent since the two
// features' backend budgets aren't guaranteed to match.
export const BOOKLET_AI_ACTION_TIMEOUT_MS = 65000;

/**
 * Token handling
 */
const tokenKeys = ["token", "accessToken", "jwt"];
const userKeys = ["user", "auth", "sessionUser", "authUser", "currentUser"];

export function getAuthToken() {
  if (typeof window === "undefined") return null;

  for (const k of tokenKeys) {
    const v = localStorage.getItem(k) || sessionStorage.getItem(k);
    if (v) return v;
  }

  for (const k of userKeys) {
    const raw = localStorage.getItem(k) || sessionStorage.getItem(k);
    if (!raw) continue;

    try {
      const obj = JSON.parse(raw);
      const t =
        obj?.token ||
        obj?.accessToken ||
        obj?.jwt ||
        obj?.data?.token ||
        obj?.data?.accessToken ||
        obj?.data?.jwt;
      if (t) return t;
    } catch {
      // ignore
    }
  }

  return null;
}

export function setAuthToken(token) {
  if (typeof window === "undefined") return;
  if (!token) return;
  localStorage.setItem("token", token);
}

export function clearAuthToken() {
  if (typeof window === "undefined") return;

  for (const k of tokenKeys) {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  }

  for (const k of userKeys) {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  }
}

// Backward-compatible aliases
export const getToken = getAuthToken;
export const clearToken = clearAuthToken;

/**
 * SUPERADMIN tenant scoping
 * - For /api/admin/* routes (admin module), backend requires tenantId.
 * - SUPERADMIN must send x-tenant-id (or query param).
 *
 * We store the currently-selected tenant in localStorage:
 *   localStorage.setItem("activeTenantId", "tnt_gapsmania_010")
 *
 * You can set this from the Tenants page when you click "View tenant" or "Use tenant".
 */
const ACTIVE_TENANT_KEY = "activeTenantId";
const ACTIVE_TENANT_NAME_KEY = "activeTenantName";
export const ACTIVE_TENANT_EVENT = "gapsmania:active-tenant-change";

function dispatchActiveTenantEvent() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(ACTIVE_TENANT_EVENT));
}

export function getActiveTenantId() {
  if (typeof window === "undefined") return null;
  const v = localStorage.getItem(ACTIVE_TENANT_KEY);
  return v ? String(v).trim() : null;
}

export function getActiveTenantName() {
  if (typeof window === "undefined") return null;
  const v = localStorage.getItem(ACTIVE_TENANT_NAME_KEY);
  return v ? String(v).trim() : null;
}

export function getActiveTenantContext() {
  return {
    tenantId: getActiveTenantId(),
    tenantName: getActiveTenantName(),
  };
}

export function setActiveTenantContext(tenantId, tenantName = "") {
  if (typeof window === "undefined") return;

  const normalizedTenantId = String(tenantId || "").trim();
  if (!normalizedTenantId) return;

  localStorage.setItem(ACTIVE_TENANT_KEY, normalizedTenantId);

  const normalizedTenantName = String(tenantName || "").trim();
  if (normalizedTenantName) {
    localStorage.setItem(ACTIVE_TENANT_NAME_KEY, normalizedTenantName);
  } else {
    localStorage.removeItem(ACTIVE_TENANT_NAME_KEY);
  }

  dispatchActiveTenantEvent();
}

export function setActiveTenantId(tenantId) {
  setActiveTenantContext(tenantId);
}

export function clearActiveTenantId() {
  if (typeof window === "undefined") return;
  localStorage.removeItem(ACTIVE_TENANT_KEY);
  localStorage.removeItem(ACTIVE_TENANT_NAME_KEY);
  dispatchActiveTenantEvent();
}

/**
 * Optional: read role from stored user/auth objects
 * We only need SUPERADMIN detection to decide attaching x-tenant-id.
 */
function getStoredUserRole() {
  if (typeof window === "undefined") return null;

  const tryParse = (raw) => {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  for (const k of userKeys) {
    const raw = localStorage.getItem(k) || sessionStorage.getItem(k);
    if (!raw) continue;

    const obj = tryParse(raw);
    const role = obj?.role || obj?.user?.role || obj?.data?.user?.role;
    if (role) return String(role);
  }

  return null;
}

function normalizeRole(raw) {
  const up = String(raw || "")
    .trim()
    .toUpperCase();
  return up === "SUPER_ADMIN" ? "SUPERADMIN" : up;
}

function isAdminModulePath(urlOrPath) {
  const p = String(urlOrPath || "");
  // axios config.url is usually relative like "/api/admin/..."
  return p.startsWith("/api/admin/") && !p.startsWith("/api/admin/system/");
}

// ---- 401 -> silent refresh-and-retry ----
// SECURITY (access-token lifetime): the backend previously issued 30-day
// access tokens specifically because no automatic refresh-and-retry existed
// here — shortening that lifetime without this would have logged users out
// (or broken in-flight requests) every time the access token expired. This
// interceptor closes that gap: on a real "token expired/invalid" 401, it
// exchanges the httpOnly refresh-token cookie for a fresh access token via
// POST /api/users/refresh (a real, rotated, DB-backed refresh already
// implemented on the backend — see auth.service.js) and retries the
// original request exactly once. Concurrent 401s share a single in-flight
// refresh call rather than each independently hitting /refresh.
let refreshPromise = null;

function requestNewAccessToken() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_BASE_URL}/api/users/refresh`, null, { withCredentials: true })
      .then((res) => {
        const newToken = res?.data?.token;
        if (!newToken) throw new Error("Refresh response did not include a token");
        setAuthToken(newToken);
        return newToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

function attachInterceptors(client) {
  client.interceptors.request.use(
    (config) => {
      const token = getAuthToken();

      config.headers = config.headers || {};

      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // ✅ SUPERADMIN: auto-attach x-tenant-id for admin-module endpoints
      // This prevents 400 "Missing tenantId" for endpoints under /api/admin/*
      const role = normalizeRole(getStoredUserRole());
      const activeTenantId = getActiveTenantId();

      if (
        role === "SUPERADMIN" &&
        activeTenantId &&
        isAdminModulePath(config.url)
      ) {
        if (!config.headers["x-tenant-id"] && !config.headers["X-Tenant-Id"]) {
          config.headers["x-tenant-id"] = activeTenantId;
        }
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "";

      const token = getAuthToken();
      const looksLikeInvalidToken =
        typeof msg === "string" &&
        /invalid token|jwt expired|token expired|unauthorized/i.test(msg);

      const config = error?.config;
      const isRefreshCall = typeof config?.url === "string" && config.url.includes("/api/users/refresh");

      if (status === 401 && token && looksLikeInvalidToken && !isRefreshCall && config && !config.__isRetryAfterRefresh) {
        try {
          const newToken = await requestNewAccessToken();
          config.__isRetryAfterRefresh = true;
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${newToken}`;
          return client(config);
        } catch {
          clearAuthToken();
          return Promise.reject(error);
        }
      }

      if (status === 401 && token && looksLikeInvalidToken) {
        clearAuthToken();
      }

      return Promise.reject(error);
    },
  );

  return client;
}

export const apiClient = attachInterceptors(
  axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    timeout: 25000,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  }),
);

export const aiClient = attachInterceptors(
  axios.create({
    baseURL: AI_BASE_URL,
    withCredentials: true,
    timeout: 35000,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  }),
);

export const pdfClient = attachInterceptors(
  axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    timeout: 35000,
    headers: {
      Accept: "*/*",
    },
  }),
);

/**
 * Error normalization
 */
export function normalizeApiError(err) {
  const status = err?.response?.status;

  const msg =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail ||
    err?.message ||
    "Request failed";

  return status ? `${status}: ${msg}` : msg;
}
